// Global AI state manager using custom events and localStorage
import { useState, useEffect } from 'react'

interface GlobalAIState {
  isAIChatOpen: boolean
  isAITerminalActive: boolean
}

class GlobalAIStateManager {
  private state: GlobalAIState = {
    isAIChatOpen: false,
    isAITerminalActive: false
  }

  private listeners: ((state: GlobalAIState) => void)[] = []
  private isInitialized = false

  constructor() {
    // Initialize on client side only
    if (typeof window !== 'undefined') {
      this.initializeFromStorage()
    }
  }

  private initializeFromStorage() {
    try {
      const saved = localStorage.getItem('globalAIState')
      if (saved) {
        const savedState = JSON.parse(saved)
        // Only restore the chat open state, not the terminal active state
        this.state = { 
          ...this.state, 
          isAIChatOpen: savedState.isAIChatOpen || false 
        }
      }
    } catch (e) {
      console.warn('Failed to load AI state from localStorage:', e)
    }
    this.isInitialized = true
  }

  getState(): GlobalAIState {
    // Ensure we're initialized before returning state
    if (!this.isInitialized && typeof window !== 'undefined') {
      this.initializeFromStorage()
    }
    return { ...this.state }
  }

  setState(newState: Partial<GlobalAIState>) {
    this.state = { ...this.state, ...newState }
    
    // Save to localStorage (only save persistent state)
    if (typeof window !== 'undefined') {
      const persistentState = {
        isAIChatOpen: this.state.isAIChatOpen
        // Don't persist isAITerminalActive as it should reset on page load
      }
      localStorage.setItem('globalAIState', JSON.stringify(persistentState))
    }
    
    // Notify listeners
    this.listeners.forEach(listener => listener(this.state))
    
    // Dispatch custom event for components that can't use the manager directly
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('globalAIStateChange', { 
        detail: this.state 
      }))
    }
  }

  subscribe(listener: (state: GlobalAIState) => void): () => void {
    this.listeners.push(listener)
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  toggleAIChat() {
    this.setState({ isAIChatOpen: !this.state.isAIChatOpen })
  }

  setAIChatOpen(open: boolean) {
    this.setState({ isAIChatOpen: open })
  }

  setAITerminalActive(active: boolean) {
    this.setState({ isAITerminalActive: active })
  }
}

// Create singleton instance
export const globalAIState = new GlobalAIStateManager()

// React hook for using global AI state
export function useGlobalAIState() {
  const [state, setState] = useState(() => globalAIState.getState())
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    // Handle hydration
    setIsHydrated(true)
    setState(globalAIState.getState())
    
    // Subscribe to state changes
    const unsubscribe = globalAIState.subscribe(setState)
    
    return unsubscribe
  }, [])

  return {
    ...state,
    isHydrated,
    toggleAIChat: () => globalAIState.toggleAIChat()
  }
} 